import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
 identityGetSelfStart,
 identityGetItemGroupsStart,
 identitySetTotpStart,
 identitySetVerificationStart,
 identityUpdateSelfStart,
 identityVerifyTotpStart,
} from '../../../../redux/identity/identitySlice';
import { RootState } from '../../../../redux/store';
import { getCookieCustom } from '../../../../utils/cookies';
import { getDefaultValues, mapGroupNamesToIds } from '../utils';
import { UserProfileFormData } from '../types';
import { GroupDTO } from '../../../../redux/identity/identityTypes';

export const useUserProfileForm = (open: boolean, onClose: () => void) => {
 const { i18n } = useTranslation();
 const dispatch = useAppDispatch();
 const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
 const [activeTab, setActiveTab] = useState(0);
 const [qrCode, setQrCode] = useState<string | null>(null);
 const [verificationCode, setVerificationCode] = useState<string>('');
 const [twoFactorAuthMethod, setTwoFactorAuthMethod] = useState<1 | 2 | null>(
  null,
 );

 const identityState = useAppSelector((state: RootState) => state.identity);
 const user = identityState.item;
 const groups = identityState.groups || [];
 const loading = identityState.loading || {};
 const success = identityState.success || {};

 const defaultValues = getDefaultValues(user, groups, i18n.language || 'lt');

 const {
  control,
  handleSubmit,
  reset,
  watch,
  setValue,
  formState: { errors },
 } = useForm<UserProfileFormData>({ defaultValues });

 const watchedTwoFactor = watch('twoFactorEnabled');

 useEffect(() => {
  if (open) {
   dispatch(identityGetSelfStart({}));
   dispatch(identityGetItemGroupsStart({}));
  }
 }, [open, dispatch]);

 useEffect(() => {
  if (user && success?.identityGetSelf) {
   const newDefaults = getDefaultValues(user, groups, i18n.language || 'lt');
   reset(newDefaults);
   setTwoFactorEnabled(user.TwoFAEnabled || false);
   setValue('language', i18n.language || 'lt');
   setValue('email', user.Email || '');
   setValue('phone', user.Phone || '');
   setValue('twoFactorEnabled', user.TwoFAEnabled || false);
   if (user.Customer) {
    setValue(
     'representing',
     `${user.Customer.Email || ''} - ${user.Customer.Name || ''}`,
    );
   }
   if (user.Groups && user.Groups.length > 0) {
    const groupNames = user.Groups.map((g: GroupDTO) => g.Name || '').filter(
     Boolean,
    );
    setValue('group', groupNames);
   }
  }
  if (success?.identityUpdateSelf && !loading?.identityGetSelf) {
   dispatch(identityGetSelfStart({}));
   onClose();
  }
  if (success?.identitySetTotp && identityState.qrCode) {
   setQrCode(identityState.qrCode?.uri);
  }
  if (success?.identityVerifyTotp) {
   dispatch(identityGetSelfStart({}));
   setQrCode(null);
   setVerificationCode('');
  }
 }, [user, groups, success, reset, setValue, i18n.language]);

 useEffect(() => {
  setTwoFactorEnabled(watchedTwoFactor);
 }, [watchedTwoFactor]);

 const handleFormSubmit = (data: UserProfileFormData) => {
  if (activeTab === 0) {
   const groupIds = mapGroupNamesToIds(data.group, groups);

   dispatch(
    identityUpdateSelfStart({
     Name: user?.Name || '',
     Surname: user?.Surname || '',
     Email: data.email,
     Phone: data.phone,
     TwoFAEnabled: data.twoFactorEnabled,
     Groups: groupIds,
     Customer: user?.Customer || null,
     ProfileImageBase64: user?.ProfileImageBase64 || null,
    }),
   );
  } else if (activeTab === 1) {
   dispatch(
    identityUpdateSelfStart({
     OldPassword: data.currentPassword,
     NewPassword: data.newPassword,
     Name: user?.Name || '',
     Surname: user?.Surname || '',
     Email: user?.Email || '',
     Phone: user?.Phone || '',
     TwoFAEnabled: user?.TwoFAEnabled || false,
     Groups: user?.Groups?.map((g: GroupDTO) => g.Id) ?? [],
     Customer: user?.Customer || null,
     ProfileImageBase64: user?.ProfileImageBase64 || null,
    }),
   );
  }
 };

 const handleSaveClick = () => {
  if (activeTab === 0) {
   handleSubmit(handleFormSubmit)();
  } else {
   const form = document.getElementById(
    'password-change-form',
   ) as HTMLFormElement;
   if (form) {
    form.requestSubmit();
   }
  }
 };

 const handleCancel = () => {
  reset(defaultValues);
  setTwoFactorEnabled(false);
  onClose();
 };

 const handleGenerateQR = () => {
  const userId = getCookieCustom('user_id');
  if (userId) {
   dispatch(
    identitySetTotpStart({
     userid: userId,
     sessionId: undefined,
    }),
   );
  }
 };

 const handle2FACodeSubmit = (fieldName: string, value: string) => {
  const userId = getCookieCustom('user_id');
  if (userId && value) {
   dispatch(
    identitySetVerificationStart({
     Method: fieldName === 'email' ? 1 : 2,
     UserId: userId,
    }),
   );
  }
 };

 const handleVerifyTotp = () => {
  const userId = getCookieCustom('user_id');
  if (userId && verificationCode) {
   dispatch(
    identityVerifyTotpStart({
     userid: userId,
     sessionId: undefined,
     code: verificationCode,
    }),
   );
   setVerificationCode('');
   setQrCode(null);
  }
 };

 return {
  control,
  errors,
  watch,
  handleSubmit,
  handleFormSubmit,
  handleSaveClick,
  handleCancel,
  activeTab,
  setActiveTab,
  user,
  groups,
  loading,
  twoFactorAuthMethod,
  setTwoFactorAuthMethod,
  twoFactorEnabled,
  setTwoFactorEnabled,
  qrCode,
  setQrCode,
  verificationCode,
  setVerificationCode,
  handleGenerateQR,
  handle2FACodeSubmit,
  handleVerifyTotp,
  i18n,
 };
};

